/**
 * manpower/useManpower/scheduleChanges.js - useManpower's pure rules: the
 * offline cache, the retry steps, moving / cleaning / pruning days and their
 * details, what a tab changed (mark* / unionChanges), putting those changes
 * back on a server copy (rebase*), and the save body and what it settled
 * (buildSave / settle). Moved out of useManpower.js, which re-exports the
 * public ones.
 */
import { ymd } from '../manpowerHelpers';

// Offline cache of the per-day details, the same role as LS_EDITS_KEY.
const LS_DETAILS_KEY = 'manpower:details';

const readLS = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key) || fallback);
  } catch {
    return JSON.parse(fallback);
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: the cache only */
  }
};

// A burst of edits goes up as one save, this long after the last of them.
const SAVE_DEBOUNCE_MS = 600;
// A failed save is tried again after 2 s, then 4, 8 ... and every 60 s from
// then on, until it goes through.
export const RETRY_FIRST_MS = 2000;
export const RETRY_MAX_MS = 60000;
export const nextRetryMs = (prev) => (prev > 0 ? Math.min(prev * 2, RETRY_MAX_MS) : RETRY_FIRST_MS);

// Parse YYYY-MM-DD as a LOCAL date - new Date("YYYY-MM-DD") is UTC midnight,
// which shifts a day back in any westward timezone.
const parseYmd = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * The one place the schedule map changes. Pure: returns a new map.
 *
 * `old` (optional) is the block being edited, { from, to, code }. Its days are
 * cleared first - but only days still carrying that block's project, so a
 * different job sitting inside the same week survives. Then `label` is written
 * over from..to (or those days cleared, if label is empty). A key is DELETED
 * to clear it: an explicit null would still count as an assignment and save.
 *
 * An unusable range (missing, or ending before it starts) changes NOTHING. It
 * used to matter less; now that a move clears the old block first, a bad range
 * would otherwise wipe someone's dates and write none back.
 */
export function moveDays(cur, name, old, from, to, label) {
  if (!from || !to || from > to) return cur;
  const next = { ...cur };
  if (old && old.from && old.to && old.code) {
    for (let d = parseYmd(old.from), e = parseYmd(old.to); d <= e; d.setDate(d.getDate() + 1)) {
      const k = `${name}|${ymd(d)}`;
      if (next[k] === old.code) delete next[k];
    }
  }
  for (let d = parseYmd(from), e = parseYmd(to); d <= e; d.setDate(d.getDate() + 1)) {
    const k = `${name}|${ymd(d)}`;
    if (label) next[k] = label;
    else delete next[k];
  }
  return next;
}

const DETAIL_FIELDS = ['location', 'projectNumber', 'status'];

/** A detail with its blank fields dropped, or null when nothing is left. */
export function cleanDetail(detail) {
  if (!detail) return null;
  const out = {};
  for (const f of DETAIL_FIELDS) {
    const v = detail[f] == null ? '' : String(detail[f]).trim();
    if (v) out[f] = v;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The details map, made to follow a moveDays. Pure: returns a new map.
 *
 * `nextEdits` is what moveDays returned for the same arguments. A day of the
 * old block that no longer has a label loses its details (a day still on some
 * other job keeps that job's); every day written gets `detail`, or nothing
 * when it is blank - and a cleared range keeps nothing. The server only
 * stores details for labelled days, so one left on an empty day would just
 * vanish on the next load.
 *
 * `detail` undefined (a caller that knows nothing of details, e.g. an older
 * screen) is not "blank": a day that keeps the label it had (`prevEdits`)
 * keeps its details, the way the server treats a PUT without them.
 */
export function moveDetails(cur, nextEdits, name, old, from, to, label, detail, prevEdits = {}) {
  if (!from || !to || from > to) return cur;
  const next = { ...cur };
  if (old && old.from && old.to && old.code) {
    for (let d = parseYmd(old.from), e = parseYmd(old.to); d <= e; d.setDate(d.getDate() + 1)) {
      const k = `${name}|${ymd(d)}`;
      if (!nextEdits[k]) delete next[k];
    }
  }
  const clean = label ? cleanDetail(detail) : null;
  for (let d = parseYmd(from), e = parseYmd(to); d <= e; d.setDate(d.getDate() + 1)) {
    const k = `${name}|${ymd(d)}`;
    if (clean) next[k] = clean;
    else if (label && detail === undefined && prevEdits[k] === label && cur[k]) next[k] = cur[k];
    else delete next[k];
  }
  return next;
}

/** Details only for days that still have a label - after a bulk label change. */
export function pruneDetails(details, edits) {
  let changed = false;
  const next = { ...details };
  for (const k of Object.keys(next))
    if (!edits[k]) {
      delete next[k];
      changed = true;
    }
  return changed ? next : details;
}

/* ── What this tab changed since its last successful save ───────────────
 * `changes` = {
 *   days:    Map 'Name|YYYY-MM-DD' -> 'label'   the day's label changed; it goes
 *                                               up with the day's details
 *                                  | 'details' only its details changed; its
 *                                               label is left to the server
 *   added, removed:  Sets of custom label names
 *   colors:          Set of projects whose colour changed
 * }
 * It holds keys only; their values are always read off the tab's state, so a
 * save sends a day as it is NOW, however many times it was edited. */

/** An empty record of changes. */
export const noChanges = () => ({ days: new Map(), added: new Set(), removed: new Set(), colors: new Set() });

// A day's label, or null: missing, null and blank all mean nothing booked.
const labelOf = (v) => (typeof v === 'string' && v.trim() ? v : null);

const keysOf = (a, b) => new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);

/** Do two details say the same thing? A blank field and a missing one are the same. PURE. */
export function sameDetail(a, b) {
  const x = cleanDetail(a) || {};
  const y = cleanDetail(b) || {};
  return DETAIL_FIELDS.every((f) => (x[f] || '') === (y[f] || ''));
}

/**
 * Note in `days` each day that differs between two schedules { edits, details }.
 * Returns how many it noted. Writes into `days` only.
 */
export function markDays(days, cur, next) {
  let n = 0;
  if (next.edits !== cur.edits) {
    for (const k of keysOf(cur.edits, next.edits)) {
      if (labelOf(cur.edits[k]) !== labelOf(next.edits[k])) {
        days.set(k, 'label');
        n += 1;
      }
    }
  }
  if (next.details !== cur.details) {
    for (const k of keysOf(cur.details, next.details)) {
      if (sameDetail(cur.details[k], next.details[k])) continue;
      if (days.get(k) !== 'label') days.set(k, 'details');
      n += 1;
    }
  }
  return n;
}

/** Note the custom label names added and removed. Returns how many. Writes into `changes` only. */
export function markNames(changes, cur, next) {
  let n = 0;
  const was = new Set(cur);
  const now = new Set(next);
  for (const name of now)
    if (!was.has(name)) {
      changes.added.add(name);
      changes.removed.delete(name);
      n += 1;
    }
  for (const name of was)
    if (!now.has(name)) {
      changes.removed.add(name);
      changes.added.delete(name);
      n += 1;
    }
  return n;
}

/** Note the projects whose colour changed. Returns how many. Writes into `changes` only. */
export function markColors(changes, cur, next) {
  let n = 0;
  for (const k of keysOf(cur, next))
    if ((cur[k] || null) !== (next[k] || null)) {
      changes.colors.add(k);
      n += 1;
    }
  return n;
}

/** Two records of changes as one; a day noted 'label' in either is 'label'. PURE. */
export function unionChanges(a, b) {
  const days = new Map(a.days);
  for (const [k, kind] of b.days) if (days.get(k) !== 'label') days.set(k, kind);
  return {
    days,
    added: new Set([...a.added, ...b.added]),
    removed: new Set([...a.removed, ...b.removed]),
    colors: new Set([...a.colors, ...b.colors]),
  };
}

/**
 * A re-read's schedule: the server's { edits, details } with this tab's
 * changed days put back on top, so nothing typed here is lost and nothing
 * saved elsewhere is hidden. A day ours by label takes our label; every day
 * ours takes our details - but, as everywhere, only while it has a label (a
 * day someone else cleared does not come back for its details). PURE.
 */
export function rebaseDays(server, local, days) {
  const edits = { ...((server && server.edits) || {}) };
  const details = { ...((server && server.details) || {}) };
  for (const [k, kind] of days) {
    if (kind === 'label') {
      if (labelOf(local.edits[k])) edits[k] = local.edits[k];
      else delete edits[k];
    }
    if (labelOf(edits[k]) && cleanDetail(local.details[k])) details[k] = local.details[k];
    else delete details[k];
  }
  return { edits, details };
}

/** A re-read's label list: the server's, with each name this tab added or removed as this tab has it. PURE. */
export function rebaseNames(server, local, changes) {
  const ours = new Set([...changes.added, ...changes.removed]);
  const have = new Set(local);
  const out = (server || []).filter((name) => !ours.has(name) || have.has(name));
  for (const name of local) if (ours.has(name) && !out.includes(name)) out.push(name);
  return out;
}

/** A re-read's colours: the server's, with each colour this tab changed as this tab has it. PURE. */
export function rebaseColors(server, local, changes) {
  const out = { ...(server || {}) };
  for (const k of changes.colors) {
    if (local[k]) out[k] = local[k];
    else delete out[k];
  }
  return out;
}

/**
 * The PATCH for these changes, from the tab's state `local` ({ edits,
 * details, customProjects, colors }): each changed day's label when its label
 * changed (null = cleared) and its details as they are now; each label name
 * this tab added or removed, the way this tab has it now (customProjectsAdd /
 * customProjectsRemove); each colour it changed, as it is now (null = back to
 * the default). The server writes only those names and colours. It used to
 * get the whole label list and colour map, and of two tabs saving a second
 * apart, the later one's lists dropped the other's new label or colour.
 * `sent` keeps what went, for settle(); `empty` means nothing in it is
 * anything the server could store. `withDetails` false (a server that sent no
 * details) leaves `details` out. null when nothing changed at all. PURE.
 */
export function buildSave(changes, local, withDetails = true) {
  const edits = {};
  const details = {};
  const days = new Map();
  for (const [k, kind] of changes.days) {
    const label = labelOf(local.edits[k]);
    const detail = label ? cleanDetail(local.details[k]) : null;
    days.set(k, { kind, label, detail });
    if (kind === 'label') edits[k] = label;
    if (withDetails && label) details[k] = detail; // null clears them
  }
  // A name can be noted both ways (a closed calendar's changes taken over):
  // it goes up the way this tab shows it, as everywhere else.
  const have = new Set(local.customProjects);
  const add = [];
  const remove = [];
  for (const name of new Set([...changes.added, ...changes.removed])) (have.has(name) ? add : remove).push(name);
  const colors = Object.fromEntries([...changes.colors].map((k) => [k, local.colors[k] || null]));
  if (!days.size && !add.length && !remove.length && !changes.colors.size) return null;
  const body = { edits };
  if (withDetails) body.details = details;
  if (add.length) body.customProjectsAdd = add;
  if (remove.length) body.customProjectsRemove = remove;
  if (changes.colors.size) body.colors = colors;
  const empty = !Object.keys(edits).length && !Object.keys(details).length && !add.length && !remove.length && !changes.colors.size;
  return { body, sent: { days, add, remove, colors }, empty };
}

/**
 * A save went through: forget each change it carried whose value is still
 * the one on screen (`local`). A day, name or colour changed again while the
 * save was out stays noted, for the next save. Writes into `changes` only.
 */
export function settle(changes, sent, local) {
  for (const [k, s] of sent.days) {
    const kind = changes.days.get(k);
    if (!kind) continue;
    if (kind === 'label' && s.kind !== 'label') continue; // relabelled while the save was out
    if (labelOf(local.edits[k]) !== s.label || !sameDetail(local.details[k], s.detail)) continue;
    changes.days.delete(k);
  }
  const have = new Set(local.customProjects);
  const done = (name) => {
    changes.added.delete(name);
    changes.removed.delete(name);
  };
  for (const name of sent.add) if (have.has(name)) done(name);
  for (const name of sent.remove) if (!have.has(name)) done(name);
  for (const [k, c] of Object.entries(sent.colors)) if (c === (local.colors[k] || null)) changes.colors.delete(k);
}

const hasChanges = (c) => c.days.size > 0 || c.added.size > 0 || c.removed.size > 0 || c.colors.size > 0;

const copyChanges = (c) => unionChanges(c, noChanges());

export { LS_DETAILS_KEY, readLS, writeLS, SAVE_DEBOUNCE_MS, hasChanges, copyChanges };
